import React from 'react';
import { Box, SpaceBetween, Badge, Icon } from '@cloudscape-design/components';
import './requestflow.scss';

const RequestFlowVisualization = ({ flowData }) => {
    if (!flowData || !flowData.layers) return null;

    // Parse detail text to create a table if it contains comma-separated key-value pairs
    const parseDetailText = (text) => {
        // Check if text contains multiple comma-separated items
        if (text.includes(',') && text.includes(':')) {
            const items = text.split(',').map(item => item.trim());
            
            // If we have multiple items, create a table
            if (items.length > 1) {
                const rows = [];
                
                items.forEach((item, idx) => {
                    const parts = item.split(':');
                    if (parts.length === 2) {
                        const key = parts[0].trim();
                        const value = parts[1].trim();
                        
                        rows.push(
                            <tr key={idx}>
                                <td className="flow-table-key">{key}</td>
                                <td className="flow-table-value">{value}</td>
                            </tr>
                        );
                    } else {
                        rows.push(
                            <tr key={idx}>
                                <td colSpan="2">{item}</td>
                            </tr>
                        );
                    }
                });
                
                return (
                    <table className="flow-detail-table">
                        <tbody>
                            {rows}
                        </tbody>
                    </table>
                );
            }
        }
        
        // Otherwise return plain text
        return text;
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'good': return 'green';
            case 'warning': return 'orange';
            case 'error': return 'red';
            case 'start': return 'blue';
            default: return 'grey';
        }
    };

    const getStatusIcon = (status) => {
        switch (status) {
            case 'good': return 'status-positive';
            case 'warning': return 'status-warning';
            case 'error': return 'status-negative';
            case 'start': return 'status-info';
            default: return 'status-info';
        }
    };

    return (
        <div className="request-flow-container">
            <Box variant="h4" padding={{ bottom: 'xs' }}>
                🔄 Request Flow
            </Box>
            
            <div className="flow-header">
                <SpaceBetween direction="horizontal" size="s">
                    <Box fontSize="body-s">
                        <strong>RID:</strong> <code className="flow-header-code">{flowData.rid}</code>
                    </Box>
                    <Box fontSize="body-s">
                        <strong>Dist:</strong> <code className="flow-header-code">{flowData.distribution}</code>
                    </Box>
                    <Box fontSize="body-s">
                        <strong>Time:</strong> <Badge color={flowData.totalTime.includes('ms') && parseInt(flowData.totalTime) > 1000 ? 'red' : 'green'}>
                            {flowData.totalTime}
                        </Badge>
                    </Box>
                    <Box fontSize="body-s">
                        <strong>Result:</strong> <Badge color={flowData.result.includes('MISS') ? 'blue' : 'green'}>
                            {flowData.result}
                        </Badge>
                    </Box>
                </SpaceBetween>
            </div>

            <div className="flow-diagram">
                {flowData.layers.map((layer, index) => (
                    <React.Fragment key={index}>
                        <div className={`flow-node flow-node-${layer.status}`}>
                            {layer.time && (
                                <div className={`flow-node-time-bar flow-time-bar-${layer.status}`}>
                                    {layer.time}
                                </div>
                            )}
                            <div className="flow-node-header">
                                <Icon name={getStatusIcon(layer.status)} size="small" />
                                <span className="flow-node-name">{layer.name}</span>
                            </div>
                            <div className="flow-node-body">
                                {layer.location && (
                                    <div className="flow-node-meta">📍 {layer.location}</div>
                                )}
                                {layer.host && (
                                    <div className="flow-node-meta">🌐 {layer.host}</div>
                                )}
                                {layer.request && (
                                    <div className="flow-node-detail">
                                        <div className="flow-detail-label">→</div>
                                        <div className="flow-detail-text">
                                            {parseDetailText(layer.request)}
                                        </div>
                                    </div>
                                )}
                                {layer.response && (
                                    <div className="flow-node-detail">
                                        <div className="flow-detail-label">←</div>
                                        <div className="flow-detail-text">
                                            {parseDetailText(layer.response)}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        
                        {index < flowData.layers.length - 1 && (
                            <div className="flow-arrow">→</div>
                        )}
                    </React.Fragment>
                ))}
            </div>
        </div>
    );
};

export default RequestFlowVisualization;
